import React, {
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  forwardRef,
  memo,
  useSyncExternalStore,
} from "react";
import {
  getGameViewArtWidth,
  subscribeGameViewArtWidth,
} from "../../../utils/gameViewArtWidth";
import { useJackpotPlates } from "../../../Games/Shared/jackpotPlates";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import {
  selectHoldAndWinActive,
  selectHoldAndWinKinds,
  selectHoldAndWinValues,
  selectHoldAndWinPoints,
} from "../../../State/Slices/holdAndWinSlice";
import { selectJackpotMultipliers } from "../../../State/Slices/gameSettingsSlice";
import { useDimensionsWhen } from "../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../pixiSettings";
import {
  useSlotRegistry,
  useSlot,
} from "../../../Contexts/SlotRegistryContext";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import HoldAndWinGameViewController, {
  HOLD_AND_WIN_GAME_VIEW_SLOTS,
  HOLD_AND_WIN_CELL,
  holdAndWinSceneScale,
  resolveHoldAndWinPlacement,
} from "./HoldAndWinGameViewController";
import HoldAndWinBoard from "../../../Games/HoldAndWin/HoldAndWinBoard";
import HoldAndWinOverlay from "../../../Games/HoldAndWin/HoldAndWinOverlay";
import HoldAndWinCounter from "../HoldAndWinCounter/HoldAndWinCounter";

// ============================================================================
// HoldAndWinGameView — the Hold & Win room. Same structure as GameViewSimple:
//
//   HoldAndWinGameView (shell)  reads the size and applies it imperatively in
//                               layout effects: the spine's placement and the
//                               no-spine backup. It follows the size only
//                               while the room is on screen (useDimensionsWhen);
//                               hidden, a resize costs it nothing, and it
//                               catches up in the render that shows it.
//   HoldAndWinScene (memo)      the spine, the 5 x 8 board and its overlay.
//                               Its props never change with the size.
//
// The board and overlay are built in the room spine's NATIVE units: one cell
// is HOLD_AND_WIN_CELL (170 x 139), and the containers keep the spine's
// scale, so a resize changes nothing inside them. The `reels` bone's layout
// scale applies to board and frame alike, so they stay in register.
//
// On screen while holdAndWin.active (raised at the wipe's midpoint), or for
// the dev preview. Hidden by alpha, never `visible` — see GameViewSimple.
// ============================================================================

const CELL_WIDTH = HOLD_AND_WIN_CELL.WIDTH;
const CELL_HEIGHT = HOLD_AND_WIN_CELL.HEIGHT;

/** Whether the room is on screen: the feature, or the dev preview. */
const useRoomVisible = () => {
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const devPreview =
    !!FEATURES_CONFIG.DEV_TOOLS?.PREVIEW_HOLD_AND_WIN_VIEW && !holdAndWinActive;
  return { visible: holdAndWinActive || devPreview, devPreview };
};

/**
 * The scene: the hold_and_win_game_view spine with the board and its overlay
 * injected into its two reel slots. Memoized; the refs are the shell's so it
 * can place them.
 */
const HoldAndWinScene = memo(
  ({
    sizeRef,
    controllerRef,
    containerRef,
    reelContainerRef,
    overlayContainerRef,
    reelSlot,
    overlaySlot,
    getPixelCellHeight,
  }) => {
    // The jackpot plates' values, on the current bet (Games/Shared/jackpotPlates).
    useJackpotPlates(controllerRef);
    // The spines over the board — the board hands landed cells to it.
    const boardOverlayRef = useRef(null);

    const slotRegistry = useSlotRegistry();
    const { holdAndWinGameViewControllerRef, holdAndWinBoardRef } = useReelRefs();

    const { visible, devPreview } = useRoomVisible();
    const kinds = useSelector(selectHoldAndWinKinds);
    const values = useSelector(selectHoldAndWinValues);
    const points = useSelector(selectHoldAndWinPoints);
    const jackpots = useSelector(selectJackpotMultipliers);

    // ─── Controller ───
    useEffect(() => {
      if (!containerRef.current || controllerRef.current) return;

      // The size as it is now — the shell writes it on every render.
      const {
        dimensions,
        symbolWidth,
        symbolHeight,
        symbolScale,
        device,
        orientation,
        layout,
        isMobileSmall,
      } = sizeRef.current;
      const realDevice = isMobileSmall
        ? PixiSettings.DEVICE.MOBILE_SMALL
        : device;

      controllerRef.current = new HoldAndWinGameViewController(
        containerRef.current,
        dimensions,
        symbolWidth,
        symbolHeight,
        symbolScale,
        realDevice,
        orientation,
        layout,
        slotRegistry,
      );
      // The shell's resize effect ran before the controller existed, so the
      // first resize happens here.
      controllerRef.current.resize(
        dimensions,
        symbolWidth,
        symbolHeight,
        symbolScale,
        realDevice,
        orientation,
        layout,
      );

      if (holdAndWinGameViewControllerRef) {
        holdAndWinGameViewControllerRef.current = controllerRef.current;
      }

      return () => {
        if (controllerRef.current) {
          controllerRef.current.destroy();
          controllerRef.current = null;
          if (holdAndWinGameViewControllerRef) {
            holdAndWinGameViewControllerRef.current = null;
          }
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─── Hidden, the room's spines sleep ─── the same flag as the alpha
    // below; flipped at the wipe's midpoint. See spineDormancy.
    useEffect(() => {
      controllerRef.current?.setDormant(!visible);
    }, [visible, controllerRef]);

    // ─── Inject the reel container into the spine's reel slot ───
    // In the slot the bone places and scales it, so whatever the shell's
    // backup gave it is reset first. If the slot goes away while the room
    // stays, the container goes back to the room for the backup to place.
    useEffect(() => {
      if (!reelSlot || !reelContainerRef.current) return;
      const node = reelContainerRef.current;
      node.position.set(0, 0);
      node.scale.set(1);
      reelSlot.addChild(node);
      const host = containerRef.current;
      return () => {
        if (node.parent !== reelSlot) return;
        reelSlot.removeChild(node);
        if (!node.destroyed && host && !host.destroyed) host.addChild(node);
      };
    }, [reelSlot, containerRef, reelContainerRef]);

    // ─── Inject the overlay container into the spine's overlay slot ───
    useEffect(() => {
      if (!overlaySlot || !overlayContainerRef.current) return;
      const node = overlayContainerRef.current;
      node.position.set(0, 0);
      node.scale.set(1);
      overlaySlot.addChild(node);
      const host = containerRef.current;
      return () => {
        if (node.parent !== overlaySlot) return;
        overlaySlot.removeChild(node);
        if (!node.destroyed && host && !host.destroyed) host.addChild(node);
      };
    }, [overlaySlot, containerRef, overlayContainerRef]);

    // ─── The lock plates live in the symbols overlay while it is up ───
    // So the open rows' coins draw over the plates and the locked rows' under
    // them (HoldAndWinGameViewController.setPlatesHost). Handed back to their
    // slots when the overlay goes.
    useEffect(() => {
      if (!visible) return undefined;
      const controller = controllerRef.current;
      const overlay = boardOverlayRef.current;
      if (!controller || !overlay) return undefined;
      controller.setPlatesHost?.(overlay);
      return () => controller.setPlatesHost?.(null);
    }, [visible, controllerRef]);

    return (
      <Container
        ref={containerRef}
        name="hold_and_win_game_view"
        sortableChildren
        // The same layer as the other two rooms — they never share the screen.
        zIndex={0}
        alpha={visible ? 1 : 0.001}
      >
        {/* Board — injected into the spine's reel slot, clipped to the window */}
        <Container
          ref={reelContainerRef}
          name="hold_and_win_reel_container"
          sortableChildren
          eventMode="passive"
        >
          {/* The 5 x 8 board, only while the room is shown. The dev preview
              also runs the board's spin loop. */}
          {visible && (
            <HoldAndWinBoard
              cellWidth={CELL_WIDTH}
              cellHeight={CELL_HEIGHT}
              overlayRef={boardOverlayRef}
              devSpinLoop={devPreview}
              kinds={kinds}
              values={values}
              points={points}
              jackpots={jackpots}
              controlRef={holdAndWinBoardRef}
            />
          )}
        </Container>

        {/* Overlay — injected into the spine's overlay slot, above the frame */}
        <Container
          ref={overlayContainerRef}
          name="hold_and_win_overlay_reel_container"
          zIndex={2}
          sortableChildren
          eventMode="passive"
        >
          {/* The landed cells' spines, laid out like the board. Above the
              frame and outside the window clip. */}
          {visible && (
            <HoldAndWinOverlay
              ref={boardOverlayRef}
              cellWidth={CELL_WIDTH}
              cellHeight={CELL_HEIGHT}
              getPixelCellHeight={getPixelCellHeight}
            />
          )}
        </Container>

        {/* The respin counter — moves itself into the room's
            respin_counter_placeholder slot (under the bottom row). */}
        <HoldAndWinCounter />
      </Container>
    );
  },
);

HoldAndWinScene.displayName = "HoldAndWinScene";

/**
 * HoldAndWinGameView: the shell. Reads the size while the room is on screen
 * and applies it imperatively; renders the memoized scene.
 */
const HoldAndWinGameView = forwardRef((props, ref) => {
  const controllerRef = useRef(null);
  const containerRef = useRef(null);
  const reelContainerRef = useRef(null);
  const overlayContainerRef = useRef(null);

  const { visible } = useRoomVisible();
  const size = useDimensionsWhen(visible);
  // The play button the box is fitted to (fitBoxToPlayButton).
  const { playButtonRef } = useReelRefs();
  const {
    dimensions,
    symbolWidth,
    symbolHeight,
    symbolScale,
    device,
    orientation,
    layout,
    isMobileSmall,
  } = size;
  // The scene reads the size once, at mount (the controller).
  const sizeRef = useRef(size);
  sizeRef.current = size;

  // The base game view's width — this room re-fits when it changes (its
  // scale is capped by it, holdAndWinSceneScale).
  const gameViewArtWidth = useSyncExternalStore(
    subscribeGameViewArtWidth,
    getGameViewArtWidth,
  );

  const reelSlot = useSlot(HOLD_AND_WIN_GAME_VIEW_SLOTS.REEL_SET);
  const overlaySlot = useSlot(HOLD_AND_WIN_GAME_VIEW_SLOTS.OVERLAY_REEL_SET);

  // The cell's height on screen, for the fire trails: one cell at this room's
  // scale (the `reels` bone's layout scale not included, as the trails expect).
  const getPixelCellHeight = useCallback(() => {
    const { dimensions: dims, layout: lay } = sizeRef.current;
    return CELL_HEIGHT * holdAndWinSceneScale(dims, lay);
  }, []);

  // ─── The spine: scale, position, skin, reel box ───
  // A layout effect: placed in the same commit as the new size, before PIXI
  // draws the next frame. Also every time the room comes on screen (a live
  // trigger), so its settings are always the ones in force — not only after
  // a resize.
  useLayoutEffect(() => {
    if (!controllerRef.current || !visible) return undefined;
    const realDevice = isMobileSmall ? PixiSettings.DEVICE.MOBILE_SMALL : device;
    controllerRef.current.resize(
      dimensions,
      symbolWidth,
      symbolHeight,
      symbolScale,
      realDevice,
      orientation,
      layout,
    );
    // ── The box fitted to the play button (2026-10-14) ── two frames on, once
    // the toolbar has placed the button for this size and PIXI has drawn it.
    // OFF (2026-10-14: "ditch this automatic way … set them the old way") —
    // the sizes it found are now the tables (HoldAndWinGameViewController).
    // let raf = requestAnimationFrame(() => {
    //   raf = requestAnimationFrame(() => {
    //     raf = null;
    //     controllerRef.current?.fitBoxToPlayButton?.(playButtonRef.current);
    //   });
    // });
    // return () => {
    //   if (raf) cancelAnimationFrame(raf);
    // };
    return undefined;
  }, [
    visible,
    dimensions,
    symbolWidth,
    symbolHeight,
    symbolScale,
    device,
    orientation,
    layout,
    isMobileSmall,
    playButtonRef,
    gameViewArtWidth,
  ]);

  // ─── BACKUP: no spine slot to inject into ───
  // The containers are then plain children of the room (the spine's parent)
  // and get the transform the spine would have given them: its position and
  // scale, times the reel box's layout factor. No frame or window clip
  // without the spine.
  useLayoutEffect(() => {
    if (reelSlot && overlaySlot) return;
    const placement = resolveHoldAndWinPlacement(dimensions, layout);
    const scale = placement.scale * placement.reelScale;
    const reels = reelContainerRef.current;
    if (reels && !reelSlot) {
      reels.position.set(placement.x, placement.y);
      reels.scale.set(scale);
    }
    const overlay = overlayContainerRef.current;
    if (overlay && !overlaySlot) {
      overlay.position.set(placement.x, placement.y);
      overlay.scale.set(scale);
    }
  }, [reelSlot, overlaySlot, dimensions, layout]);

  return (
    <HoldAndWinScene
      sizeRef={sizeRef}
      controllerRef={controllerRef}
      containerRef={containerRef}
      reelContainerRef={reelContainerRef}
      overlayContainerRef={overlayContainerRef}
      reelSlot={reelSlot}
      overlaySlot={overlaySlot}
      getPixelCellHeight={getPixelCellHeight}
    />
  );
});

HoldAndWinGameView.displayName = "HoldAndWinGameView";

export default HoldAndWinGameView;
